import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { AnomalyPage } from './pages/anomalies/AnomalyPage';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { SECTION_LANDING, worksWithoutTenant } from './auth/tenantlessRoutes';
import { EmptyState } from './components/common/EmptyState';
import { LoadingSkeleton } from './components/common/LoadingSkeleton';
import { useAuth, hasRole } from './auth/AuthProvider';
import { RequirePartner } from './auth/RequirePartner';
import { RequirePlatform } from './auth/RequirePlatform';
import { NoUsableTenantPage } from './pages/NoUsableTenantPage';
import { ChooseTenantPage } from './pages/ChooseTenantPage';
import { landingFor } from './auth/requireAuthLanding';
import { RequireFeature } from './features/RequireFeature';
import { FleetTachoPage } from './pages/FleetTachoPage';

// Route-level code splitting: each page is its own chunk, loaded on demand.
function named<K extends string>(p: Promise<Record<K, ComponentType>>, key: K) {
  return p.then((m) => ({ default: m[key] }));
}

const LoginPage = lazy(() => named(import('./pages/LoginPage'), 'LoginPage'));
const AcceptInvitationPage = lazy(() =>
  named(import('./pages/AcceptInvitationPage'), 'AcceptInvitationPage'),
);
const DashboardPage = lazy(() => named(import('./pages/DashboardPage'), 'DashboardPage'));
const IncidentQueuePage = lazy(() =>
  named(import('./pages/IncidentQueuePage'), 'IncidentQueuePage'),
);
const IncidentDetailPage = lazy(() =>
  named(import('./pages/IncidentDetailPage'), 'IncidentDetailPage'),
);
const DailyHeadlinesPage = lazy(() =>
  named(import('./pages/DailyHeadlinesPage'), 'DailyHeadlinesPage'),
);
const OutcomeAnalyticsPage = lazy(() =>
  named(import('./pages/OutcomeAnalyticsPage'), 'OutcomeAnalyticsPage'),
);
const CanIntelligencePage = lazy(() =>
  named(import('./pages/CanIntelligencePage'), 'CanIntelligencePage'),
);
const CopilotPage = lazy(() => named(import('./pages/copilot/CopilotPage'), 'CopilotPage'));
const SettingsPage = lazy(() => named(import('./pages/settings/SettingsPage'), 'SettingsPage'));
const VehicleClassesPage = lazy(() =>
  named(import('./pages/admin/VehicleClassesPage'), 'VehicleClassesPage'),
);
const VehicleImportPage = lazy(() =>
  named(import('./pages/vehicles/VehicleImportPage'), 'VehicleImportPage'),
);
const DriversHoursRulesetsPage = lazy(() =>
  named(import('./pages/admin/DriversHoursRulesetsPage'), 'DriversHoursRulesetsPage'),
);
const SubTenantsPage = lazy(() => named(import('./pages/admin/SubTenantsPage'), 'SubTenantsPage'));
const RecommendationQueuePage = lazy(() =>
  named(import('./pages/RecommendationQueuePage'), 'RecommendationQueuePage'),
);
const RecommendationDetailPage = lazy(() =>
  named(import('./pages/RecommendationDetailPage'), 'RecommendationDetailPage'),
);
const VehicleListPage = lazy(() => named(import('./pages/VehicleListPage'), 'VehicleListPage'));
const VehicleDetailPage = lazy(() =>
  named(import('./pages/VehicleDetailPage'), 'VehicleDetailPage'),
);
const CorrelationConfigPage = lazy(() =>
  named(import('./pages/CorrelationConfigPage'), 'CorrelationConfigPage'),
);
const GeofencesPage = lazy(() => named(import('./pages/admin/GeofencesPage'), 'GeofencesPage'));
const DriverListPage = lazy(() => named(import('./pages/DriverListPage'), 'DriverListPage'));
const InboundEventsPage = lazy(() =>
  import('./pages/admin/InboundEventsPage').then((m) => ({ default: m.InboundEventsPage })),
);
const DriverDetailPage = lazy(() => named(import('./pages/DriverDetailPage'), 'DriverDetailPage'));
const DriverCreatePage = lazy(() =>
  named(import('./pages/drivers/DriverCreatePage'), 'DriverCreatePage'),
);
const DriverEditPage = lazy(() =>
  named(import('./pages/drivers/DriverEditPage'), 'DriverEditPage'),
);
const PolicyManagementPage = lazy(() =>
  named(import('./pages/PolicyManagementPage'), 'PolicyManagementPage'),
);
const AuditViewerPage = lazy(() => named(import('./pages/AuditViewerPage'), 'AuditViewerPage'));
const PromptRegistryPage = lazy(() =>
  named(import('./pages/PromptRegistryPage'), 'PromptRegistryPage'),
);
const LlmExecutionViewerPage = lazy(() =>
  named(import('./pages/LlmExecutionViewerPage'), 'LlmExecutionViewerPage'),
);
const ProviderConnectionsPage = lazy(() =>
  named(import('./pages/ProviderConnectionsPage'), 'ProviderConnectionsPage'),
);
const TenantSettingsPage = lazy(() =>
  named(import('./pages/TenantSettingsPage'), 'TenantSettingsPage'),
);
const NotificationSettingsPage = lazy(() =>
  named(import('./pages/NotificationSettingsPage'), 'NotificationSettingsPage'),
);
const NotificationPolicyPage = lazy(() =>
  named(import('./pages/admin/NotificationPolicyPage'), 'NotificationPolicyPage'),
);
const BrandingPage = lazy(() => named(import('./pages/admin/BrandingPage'), 'BrandingPage'));
const ScheduledReportsPage = lazy(() =>
  named(import('./pages/admin/ScheduledReportsPage'), 'ScheduledReportsPage'),
);
const PlatformNotificationsPage = lazy(() =>
  named(import('./pages/platform/PlatformNotificationsPage'), 'PlatformNotificationsPage'),
);
const MemberAdministrationPage = lazy(() =>
  named(import('./pages/MemberAdministrationPage'), 'MemberAdministrationPage'),
);

// Platform administration (UI-2). Each is wrapped in RequirePlatform below.
const VehicleCreatePage = lazy(() =>
  named(import('./pages/fleet/VehicleCreatePage'), 'VehicleCreatePage'),
);
const VehicleEditPage = lazy(() =>
  named(import('./pages/fleet/VehicleEditPage'), 'VehicleEditPage'),
);

const PlatformAuditPage = lazy(() =>
  named(import('./pages/platform/PlatformAuditPage'), 'PlatformAuditPage'),
);
const CanOperationsPage = lazy(() =>
  named(import('./pages/operations/CanOperationsPage'), 'CanOperationsPage'),
);
const PipelineHealthPage = lazy(() =>
  named(import('./pages/platform/PipelineHealthPage'), 'PipelineHealthPage'),
);
const LogViewerPage = lazy(() => named(import('./pages/platform/LogViewerPage'), 'LogViewerPage'));
const LogDashboardPage = lazy(() =>
  named(import('./pages/platform/LogDashboardPage'), 'LogDashboardPage'),
);
const PlatformAlertsPage = lazy(() =>
  named(import('./pages/platform/PlatformAlertsPage'), 'PlatformAlertsPage'),
);
const UserListPage = lazy(() => named(import('./pages/platform/UserListPage'), 'UserListPage'));
const ActiveSessionsPage = lazy(() =>
  named(import('./pages/platform/ActiveSessionsPage'), 'ActiveSessionsPage'),
);
const UserCreatePage = lazy(() =>
  named(import('./pages/platform/UserCreatePage'), 'UserCreatePage'),
);
const UserDetailPage = lazy(() =>
  named(import('./pages/platform/UserDetailPage'), 'UserDetailPage'),
);

const TenantListPage = lazy(() =>
  named(import('./pages/platform/TenantListPage'), 'TenantListPage'),
);
const TenantCreatePage = lazy(() =>
  named(import('./pages/platform/TenantCreatePage'), 'TenantCreatePage'),
);
const PartnerPeoplePage = lazy(() =>
  named(import('./pages/partner/PartnerPeoplePage'), 'PartnerPeoplePage'),
);
const PartnerBrandingPage = lazy(() =>
  named(import('./pages/partner/PartnerBrandingPage'), 'PartnerBrandingPage'),
);
const PartnerUnitsPage = lazy(() =>
  named(import('./pages/partner/PartnerUnitsPage'), 'PartnerUnitsPage'),
);
const PartnerCompaniesPage = lazy(() =>
  named(import('./pages/partner/PartnerCompaniesPage'), 'PartnerCompaniesPage'),
);
const PartnerResellersPage = lazy(() =>
  named(import('./pages/partner/PartnerResellersPage'), 'PartnerResellersPage'),
);
const PartnerImportPage = lazy(() =>
  named(import('./pages/imports/PartnerImportPage'), 'PartnerImportPage'),
);
const PlatformImportPage = lazy(() =>
  named(import('./pages/imports/PlatformImportPage'), 'PlatformImportPage'),
);
const WhiteLabelListPage = lazy(() =>
  named(import('./pages/platform/WhiteLabelListPage'), 'WhiteLabelListPage'),
);
const WhiteLabelCreatePage = lazy(() =>
  named(import('./pages/platform/WhiteLabelCreatePage'), 'WhiteLabelCreatePage'),
);
const WhiteLabelDetailPage = lazy(() =>
  named(import('./pages/platform/WhiteLabelDetailPage'), 'WhiteLabelDetailPage'),
);
const TenantDetailPage = lazy(() =>
  named(import('./pages/platform/TenantDetailPage'), 'TenantDetailPage'),
);
const TenantEditPage = lazy(() =>
  named(import('./pages/platform/TenantEditPage'), 'TenantEditPage'),
);

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, tenantId, tenantChoices } = useAuth();
  const { pathname } = useLocation();
  if (!user) return <Navigate to="/login" replace />;

  /*
   * Signed in, and not placed anywhere. TWO different reasons, and they used to
   * render identically.
   *
   * `inferActiveTenant` returns nothing in three cases: no memberships at all,
   * every membership suspended or archived, or MORE THAN ONE usable — where it
   * declines to guess, correctly, because landing somebody in an arbitrary
   * organisation is worse than asking. The comment that used to sit here named
   * only the first two and called the result "nowhere to go".
   *
   * So a person reaching a dozen organisations was told they did not belong to
   * one. That reads as an account fault and sends them to an administrator with
   * nothing to fix. It is also invisible on a second visit — the choice persists
   * in localStorage — which makes it look intermittent.
   *
   * Nobody hit it while a tenant administrator held exactly two memberships (a
   * parent and its Default sub, one of them operational). A white-label partner
   * administrator, a member of every company they look after, hits it on first
   * login: docs/architecture/ajnaview-company-reseller-mapping.md §7.
   *
   * A platform administrator legitimately has no tenant — that is how they reach
   * the platform screens — so none of this applies to them.
   */
  const landing = landingFor({
    tenantId,
    isPlatformAdmin: user.isPlatformAdmin,
    choices: tenantChoices,
    // A partner administrator with no companies yet is the fourth case: nowhere
    // to go, and a console to go to. Without this the one person who must
    // create the first company is told they do not belong to a tenant.
    administersWhiteLabel: (user.whiteLabels ?? []).length > 0,
  });
  if (landing === 'CHOOSE') return <ChooseTenantPage />;
  if (landing === 'NONE') return <NoUsableTenantPage />;
  /*
   * A partner administrator with no tenant goes to their own console.
   *
   * The redirect rather than a render, so the URL says where they are — and
   * guarded on the path so it cannot loop, since the console itself sits behind
   * this component.
   *
   * Without it `/` renders the operations dashboard: five zeros, "No incidents"
   * and "No data", for somebody who has no fleet because they are not in a
   * tenant at all. Every one of those numbers is a lie of a particular kind —
   * it looks like an empty fleet rather than like a question nobody asked.
   * Found by signing in as one.
   */
  if (landing === 'PARTNER' && !pathname.startsWith('/partner')) {
    return <Navigate to="/partner/companies" replace />;
  }

  /*
   * A PLATFORM administrator with no tenant, standing on a tenant-scoped page.
   *
   * Every page outside the prefixes below scopes its requests to a tenant, and
   * without one the server refuses with *"Platform admin must specify
   * X-Tenant-Id"* — correctly, since it will not infer cross-tenant access. On
   * screen that is a warning panel where a fleet should be, which reads as a
   * permissions fault rather than as standing in the wrong place.
   *
   * Reported after *Leave tenant* was pressed on `/incidents`: leaving does not
   * navigate, so the reader stayed on a page that could no longer work. A
   * REDIRECT rather than making the button navigate, because it also covers the
   * ways somebody arrives without pressing anything — a bookmark, the back
   * button, a link from a colleague.
   *
   * Same shape as the partner redirect above, and guarded on the path for the
   * same reason: `/` renders `PlatformHome`, so sending them there cannot loop.
   */
  if (tenantId === null && user.isPlatformAdmin && !worksWithoutTenant(pathname)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function RequireRole({ allowed, children }: { allowed: string[]; children: ReactNode }) {
  const { role, user } = useAuth();
  if (!hasRole(role, ...allowed) && !user?.isPlatformAdmin) {
    return (
      <EmptyState
        title="You do not have access"
        message="This area is restricted to administrators. Ask a tenant admin if you need access."
      />
    );
  }
  return <>{children}</>;
}

export function AppRoutes() {
  const { user } = useAuth();
  return (
    <Suspense fallback={<LoadingSkeleton rows={8} />}>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
        {/*
          ⚠️ OUTSIDE `RequireAuth`, and deliberately NOT redirected away when a
          session exists. The reader has no account to sign in with yet — that
          is the point — and bouncing them to `/login` would lose the token in
          the URL and with it their only way in. Somebody already signed in who
          opens an invitation for a COLLEAGUE is a real case: the token decides
          whose password is set, not the session.
        */}
        <Route path="/accept-invitation/:token" element={<AcceptInvitationPage />} />
        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route path="/" element={<DashboardPage />} />
          <Route path="/incidents" element={<IncidentQueuePage />} />
          <Route path="/incidents/:id" element={<IncidentDetailPage />} />
          <Route path="/recommendations" element={<RecommendationQueuePage />} />
          {/* Declared before :id so "outcomes" is not captured as an id, mirroring
              the ordering the API controller uses for the same reason. */}
          <Route path="/recommendations/outcomes" element={<OutcomeAnalyticsPage />} />
          <Route
            path="/operations/can-intelligence"
            element={
              <RequireFeature feature="CAN_TELEMETRY">
                <CanIntelligencePage />
              </RequireFeature>
            }
          />
          {/* Both gates, in this order: the entitlement is what the customer
              bought, the role is who inside the customer may use it. The API
              refuses independently — neither of these is the enforcement. */}
          <Route
            path="/copilot"
            element={
              <RequireFeature feature="AI_COPILOT">
                <RequireRole allowed={['TENANT_ADMIN', 'OPERATIONS_MANAGER']}>
                  <CopilotPage />
                </RequireRole>
              </RequireFeature>
            }
          />
          {/* Declared before /vehicles/:id so "classes" is not captured as an id. */}
          <Route path="/vehicles/classes" element={<VehicleClassesPage />} />
          {/*
           * Declared before /vehicles/:id for the same reason as "classes" above.
           *
           * ⚠️ Feature-gated, matching the server: the two alerts a ruleset moves read
           * TACHO-tier counters, which a tenant without TACHOGRAPH has dropped at ingestion —
           * so an ungated screen would accept a limit and produce no alerts.
           */}
          <Route
            path="/vehicles/drivers-hours-rulesets"
            element={
              <RequireFeature feature="TACHOGRAPH">
                <DriversHoursRulesetsPage />
              </RequireFeature>
            }
          />
          {/*
           * Declared before /vehicles/:id for the same reason as "classes" above.
           *
           * ⚠️ BOTH gates, in the same order as the controller: feature outside, role
           * inside. `TENANT_ADMIN` alone is NARROWER than the New vehicle dialog beside it,
           * deliberately — creating one wrong vehicle is a row to archive, and a file
           * creates five thousand whose plates stay reserved afterwards (D3). Matching the
           * server exactly is what keeps this from being either a screen that 403s on every
           * press or a UI stricter than the API.
           */}
          <Route
            path="/vehicles/import"
            element={
              <RequireFeature feature="BULK_VEHICLE_IMPORT">
                <RequireRole allowed={['TENANT_ADMIN']}>
                  <VehicleImportPage />
                </RequireRole>
              </RequireFeature>
            }
          />
          <Route path="/admin/sub-tenants" element={<SubTenantsPage />} />
          <Route path="/recommendations/:id" element={<RecommendationDetailPage />} />
          <Route path="/vehicles" element={<VehicleListPage />} />{' '}
          {/* Write routes are role-gated server-side; RequireRole keeps the
              UI consistent with that rather than substituting for it. */}
          <Route
            path="/vehicles/new"
            element={
              <RequireRole allowed={['TENANT_ADMIN', 'OPERATIONS_MANAGER']}>
                <VehicleCreatePage />
              </RequireRole>
            }
          />
          <Route
            path="/vehicles/:id/edit"
            element={
              <RequireRole allowed={['TENANT_ADMIN', 'OPERATIONS_MANAGER']}>
                <VehicleEditPage />
              </RequireRole>
            }
          />
          <Route path="/vehicles/:id" element={<VehicleDetailPage />} />
          {/* Drivers. Writes are role-gated server-side; RequireRole keeps the
              UI consistent with that rather than substituting for it. */}
          <Route path="/drivers" element={<DriverListPage />} />
          <Route
            path="/drivers/new"
            element={
              <RequireRole allowed={['TENANT_ADMIN', 'OPERATIONS_MANAGER']}>
                <DriverCreatePage />
              </RequireRole>
            }
          />
          <Route
            path="/drivers/:id/edit"
            element={
              <RequireRole allowed={['TENANT_ADMIN', 'OPERATIONS_MANAGER']}>
                <DriverEditPage />
              </RequireRole>
            }
          />
          <Route path="/drivers/:id" element={<DriverDetailPage />} />
          <Route path="/policies" element={<PolicyManagementPage />} />
          {/*
           * ⚠️ FIVE places already linked to `/policies/:id` and this route did not
           * exist, so every one of them was a dead link to "Page not found" — the
           * incident detail's policy-evaluation card, the recommendation's rule panel
           * (twice), its evidence list and the policy-learning card. All of them are
           * the "nobody can retune a UUID" fix in reverse: the name was made a link
           * precisely so a reader could reach the rule, and the link went nowhere.
           *
           * The same PAGE, because it already holds the detail panel and selects by id
           * — a second route rendering a second component would be two surfaces for one
           * policy, which is the shape this codebase keeps paying for.
           */}
          <Route path="/policies/:policyId" element={<PolicyManagementPage />} />
          <Route path="/prompts" element={<PromptRegistryPage />} />
          <Route path="/executions" element={<LlmExecutionViewerPage />} />
          <Route
            path="/settings"
            element={
              /*
               * ⚠️ NO `RequireRole`. The page renders only what `isNavItemVisible` allows, so a
               * VIEWER reaching it sees the "nothing here for your role" state rather than a 403 —
               * and each page behind it keeps its own guard, which is what actually protects it.
               * A role gate here would ALSO have to be kept in step with seventeen items' roles,
               * which is a second copy of a decision `navVisibility` already makes.
               */
              <SettingsPage />
            }
          />
          <Route
            path="/audit"
            element={
              <RequireRole allowed={['TENANT_ADMIN', 'OPERATIONS_MANAGER']}>
                <AuditViewerPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/providers"
            element={
              <RequireRole allowed={['TENANT_ADMIN']}>
                <ProviderConnectionsPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/correlation"
            element={
              <RequireRole allowed={['TENANT_ADMIN']}>
                <CorrelationConfigPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/inbound-events"
            element={
              <RequireRole allowed={['TENANT_ADMIN']}>
                <InboundEventsPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/geofences"
            element={
              <RequireRole allowed={['TENANT_ADMIN', 'OPERATIONS_MANAGER']}>
                <GeofencesPage />
              </RequireRole>
            }
          />
          {/*
            No RequireRole: a person's own notification settings are available to
            every authenticated role including VIEWER. An administrator cannot
            edit them on their behalf (D29), so gating this would leave no route.
          */}
          <Route path="/account/notifications" element={<NotificationSettingsPage />} />
          {/*
            Policy is TENANT_ADMIN; reading and the diagnostic extend to
            OPERATIONS_MANAGER, who fields "I did not get the alert" and needs the
            answer without the power to change policy. The server enforces the
            split per route — this allows both in and lets it refuse the writes.
          */}
          {/* Gated for the same reason as branding (D30). */}
          <Route
            path="/admin/reports"
            element={
              <RequireFeature feature="SCHEDULED_REPORTS">
                <RequireRole allowed={['TENANT_ADMIN', 'OPERATIONS_MANAGER']}>
                  <ScheduledReportsPage />
                </RequireRole>
              </RequireFeature>
            }
          />
          {/*
            Feature-gated, unlike the alerting settings screen. That one loads
            without its entitlement because it describes something the person still
            receives; this describes something the tenant does not have.
          */}
          <Route
            path="/admin/branding"
            element={
              <RequireFeature feature="WHITE_LABEL">
                <RequireRole allowed={['TENANT_ADMIN', 'OPERATIONS_MANAGER']}>
                  <BrandingPage />
                </RequireRole>
              </RequireFeature>
            }
          />
          <Route
            path="/admin/notifications"
            element={
              <RequireRole allowed={['TENANT_ADMIN', 'OPERATIONS_MANAGER']}>
                <NotificationPolicyPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <RequireRole allowed={['TENANT_ADMIN']}>
                <TenantSettingsPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/members"
            element={
              <RequireRole allowed={['TENANT_ADMIN']}>
                <MemberAdministrationPage />
              </RequireRole>
            }
          />
          {/*
            Compatibility redirect for the pre-rename path. Explicit and
            temporary: there is ONE page, and the old URL simply forwards to it
            so existing bookmarks and links do not break. Remove once no
            external references remain.
          */}
          <Route path="/admin/users" element={<Navigate to="/admin/members" replace />} />
          {/*
            "Administration" is a nav SECTION, not a destination, so nothing
            links to /admin — but it is an obvious thing to type, and it
            dead-ended on a bare "Page not found" while the breadcrumb happily
            read "Home › Administration", which makes a section look like a
            broken page. Settings is the one entry every administrator can
            reach; the deeper pages are role-gated.
          */}
          <Route path="/admin" element={<Navigate to="/admin/settings" replace />} />
          {/*
            Platform scope. Every route is individually wrapped so the guard is
            never something a new page can forget to opt into, and the trailing
            wildcard keeps unknown /platform paths behind the same denial —
            otherwise a non-platform user would get a not-found that reveals the
            route space exists.
          */}
          <Route
            path="/platform/tenants"
            element={
              <RequirePlatform>
                <TenantListPage />
              </RequirePlatform>
            }
          />
          <Route
            path="/platform/imports"
            element={
              <RequirePlatform>
                <PlatformImportPage />
              </RequirePlatform>
            }
          />
          <Route
            path="/platform/tenants/new"
            element={
              <RequirePlatform>
                <TenantCreatePage />
              </RequirePlatform>
            }
          />
          <Route
            path="/platform/tenants/:id"
            element={
              <RequirePlatform>
                <TenantDetailPage />
              </RequirePlatform>
            }
          />
          <Route
            path="/platform/tenants/:id/edit"
            element={
              <RequirePlatform>
                <TenantEditPage />
              </RequirePlatform>
            }
          />
          {/*
            The partner console. Guarded by `RequirePartner`, not
            `RequirePlatform`: partner authority is a membership in a partner,
            which a platform administrator does not have and a tenant role does
            not confer.
          */}
          <Route
            path="/partner/companies"
            element={
              <RequirePartner>
                <PartnerCompaniesPage />
              </RequirePartner>
            }
          />
          {/*
            ⚠️ **The BARE section paths, which 404'd.** Generated from `SECTION_LANDING` rather
            than written out, so a section gaining a prefix cannot keep the hole — see that
            constant for why `/partner` fell through the loop guard.
          */}
          {Object.entries(SECTION_LANDING).map(([prefix, to]) => (
            <Route key={prefix} path={prefix} element={<Navigate to={to} replace />} />
          ))}
          <Route
            path="/partner/imports"
            element={
              <RequirePartner>
                <PartnerImportPage />
              </RequirePartner>
            }
          />
          <Route
            path="/partner/branding"
            element={
              <RequirePartner>
                <PartnerBrandingPage />
              </RequirePartner>
            }
          />
          <Route
            path="/partner/units"
            element={
              <RequirePartner>
                <PartnerUnitsPage />
              </RequirePartner>
            }
          />
          <Route
            path="/partner/resellers"
            element={
              <RequirePartner>
                <PartnerResellersPage />
              </RequirePartner>
            }
          />
          <Route
            path="/partner/people"
            element={
              <RequirePartner>
                <PartnerPeoplePage />
              </RequirePartner>
            }
          />
          {/*
            White-label partners — the tier above tenants. `new` is declared
            BEFORE `:id`, or React Router would match it as a partner id and
            the create page would be unreachable behind a 404 for a partner
            that does not exist.
          */}
          <Route
            path="/platform/white-labels"
            element={
              <RequirePlatform>
                <WhiteLabelListPage />
              </RequirePlatform>
            }
          />
          <Route
            path="/platform/white-labels/new"
            element={
              <RequirePlatform>
                <WhiteLabelCreatePage />
              </RequirePlatform>
            }
          />
          <Route
            path="/platform/white-labels/:id"
            element={
              <RequirePlatform>
                <WhiteLabelDetailPage />
              </RequirePlatform>
            }
          />
          <Route
            path="/platform/users"
            element={
              <RequirePlatform>
                <UserListPage />
              </RequirePlatform>
            }
          />
          <Route
            path="/platform/sessions"
            element={
              <RequirePlatform>
                <ActiveSessionsPage />
              </RequirePlatform>
            }
          />
          <Route
            path="/platform/users/new"
            element={
              <RequirePlatform>
                <UserCreatePage />
              </RequirePlatform>
            }
          />
          <Route
            path="/platform/users/:id"
            element={
              <RequirePlatform>
                <UserDetailPage />
              </RequirePlatform>
            }
          />
          <Route
            path="/operations/can-operations"
            element={
              <RequireFeature feature="CAN_TELEMETRY">
                <CanOperationsPage />
              </RequireFeature>
            }
          />
          <Route path="/news" element={<DailyHeadlinesPage />} />
          <Route
            path="/operations/anomalies"
            element={
              <RequireFeature feature="ANOMALY_DETECTION">
                <AnomalyPage />
              </RequireFeature>
            }
          />
          <Route
            path="/operations/tachograph"
            element={
              <RequireFeature feature="TACHOGRAPH">
                <FleetTachoPage />
              </RequireFeature>
            }
          />
          <Route
            path="/platform/audit"
            element={
              <RequirePlatform>
                <PlatformAuditPage />
              </RequirePlatform>
            }
          />
          <Route
            path="/platform/notifications"
            element={
              <RequirePlatform>
                <PlatformNotificationsPage />
              </RequirePlatform>
            }
          />
          <Route
            path="/platform/pipeline-health"
            element={
              <RequirePlatform>
                <PipelineHealthPage />
              </RequirePlatform>
            }
          />
          <Route
            path="/platform/logs"
            element={
              <RequirePlatform>
                <LogViewerPage />
              </RequirePlatform>
            }
          />
          <Route
            path="/platform/alerts"
            element={
              <RequirePlatform>
                <PlatformAlertsPage />
              </RequirePlatform>
            }
          />
          <Route
            path="/platform/log-dashboard"
            element={
              <RequirePlatform>
                <LogDashboardPage />
              </RequirePlatform>
            }
          />
          <Route
            path="/platform/*"
            element={
              <RequirePlatform>
                <EmptyState
                  title="Page not found"
                  message="This platform administration page does not exist yet."
                />
              </RequirePlatform>
            }
          />
          <Route
            path="*"
            element={
              <EmptyState title="Page not found" message="The page you requested does not exist." />
            }
          />
        </Route>
      </Routes>
    </Suspense>
  );
}
